import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { AddCircleOutline, Calculate, Delete } from '@mui/icons-material';

/**
 * Phase 4.1 — Tamisage sub-form for the mechanical-preparation step.
 * Mirrors PhysicoChimiqueAnalystDashboard.calculateGranulometrie:
 *   - rows = { size_um, mass_g } pairs (sorted descending by size)
 *   - refus_percent       = mass / total_mass * 100
 *   - cumulative_refus    = running sum (descending)
 *   - cumulative_passing  = 100 - cumulative_refus
 *   - D80 = linear interpolation across the 80% passing crossover
 *   - D50 = linear interpolation across the 50% passing crossover
 *
 * The result is exposed via onChange so the parent can persist it under
 * metadonnees.tamisage when completing the sieving step.
 */

export interface TamisageRow {
  size_um: number;
  mass_g: number;
  refus_percent?: number;
  cumulative_refus?: number;
  cumulative_passing?: number;
}

export interface TamisageData {
  mesh_sizes: TamisageRow[];
  total_mass: number;
  d80: number | null;
  d50: number | null;
  notes?: string;
}

const DEFAULT_MESHES: TamisageRow[] = [
  { size_um: 2000, mass_g: 0 },
  { size_um: 1000, mass_g: 0 },
  { size_um: 500, mass_g: 0 },
  { size_um: 250, mass_g: 0 },
  { size_um: 125, mass_g: 0 },
  { size_um: 75, mass_g: 0 },
];

interface Props {
  initial?: Partial<TamisageData>;
  onChange?: (data: TamisageData) => void;
  disabled?: boolean;
}

export const TamisageSubForm: React.FC<Props> = ({ initial, onChange, disabled }) => {
  const [rows, setRows] = useState<TamisageRow[]>(
    initial?.mesh_sizes && initial.mesh_sizes.length > 0
      ? initial.mesh_sizes
      : DEFAULT_MESHES,
  );
  const [notes, setNotes] = useState<string>(initial?.notes || '');

  const computed = useMemo((): TamisageData => {
    const sorted = [...rows].sort((a, b) => (b.size_um || 0) - (a.size_um || 0));
    const totalMass = sorted.reduce((s, r) => s + (Number(r.mass_g) || 0), 0);
    if (totalMass === 0) {
      return {
        mesh_sizes: sorted.map((r) => ({
          ...r,
          refus_percent: 0,
          cumulative_refus: 0,
          cumulative_passing: 100,
        })),
        total_mass: 0,
        d80: null,
        d50: null,
        notes,
      };
    }
    let cum = 0;
    const annotated = sorted.map((r) => {
      const mass = Number(r.mass_g) || 0;
      const pct = (mass / totalMass) * 100;
      cum += pct;
      return {
        ...r,
        refus_percent: Math.round(pct * 100) / 100,
        cumulative_refus: Math.round(cum * 100) / 100,
        cumulative_passing: Math.round((100 - cum) * 100) / 100,
      };
    });

    const interpolate = (target: number): number | null => {
      for (let i = 0; i < annotated.length - 1; i++) {
        const a = annotated[i];
        const b = annotated[i + 1];
        if ((a.cumulative_passing ?? 0) >= target && (b.cumulative_passing ?? 0) < target) {
          const ratio =
            (target - (b.cumulative_passing ?? 0)) /
            ((a.cumulative_passing ?? 0) - (b.cumulative_passing ?? 0));
          return Math.round(b.size_um + ratio * (a.size_um - b.size_um));
        }
      }
      return null;
    };

    return {
      mesh_sizes: annotated,
      total_mass: Math.round(totalMass * 100) / 100,
      d80: interpolate(80),
      d50: interpolate(50),
      notes,
    };
  }, [rows, notes]);

  useEffect(() => {
    onChange?.(computed);
  }, [computed, onChange]);

  const updateRow = (idx: number, field: keyof TamisageRow, value: number) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const addRow = () => setRows((p) => [...p, { size_um: 0, mass_g: 0 }]);
  const removeRow = (idx: number) => setRows((p) => p.filter((_, i) => i !== idx));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Calculate color="primary" />
        <Typography variant="h6">Tamisage / Granulométrie</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Saisissez la masse retenue par chaque tamis (en grammes). Les pourcentages,
        D80 et D50 sont calculés automatiquement.
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tamis (μm)</TableCell>
              <TableCell align="right">Masse retenue (g)</TableCell>
              <TableCell align="right">% Refus</TableCell>
              <TableCell align="right">% Cumulatif refus</TableCell>
              <TableCell align="right">% Passant</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {computed.mesh_sizes.map((r, idx) => (
              <TableRow key={idx} hover>
                <TableCell>
                  <TextField
                    type="number"
                    size="small"
                    value={r.size_um}
                    disabled={disabled}
                    onChange={(e) => updateRow(idx, 'size_um', Number(e.target.value) || 0)}
                    inputProps={{ min: 0, step: 1, style: { width: 80 } }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    type="number"
                    size="small"
                    value={r.mass_g}
                    disabled={disabled}
                    onChange={(e) => updateRow(idx, 'mass_g', Number(e.target.value) || 0)}
                    inputProps={{ min: 0, step: 0.01, style: { width: 80, textAlign: 'right' } }}
                  />
                </TableCell>
                <TableCell align="right">{(r.refus_percent ?? 0).toFixed(2)}</TableCell>
                <TableCell align="right">{(r.cumulative_refus ?? 0).toFixed(2)}</TableCell>
                <TableCell align="right">{(r.cumulative_passing ?? 0).toFixed(2)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Supprimer ce tamis">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => removeRow(idx)}
                        disabled={disabled || rows.length <= 1}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 1 }}>
        <Button
          startIcon={<AddCircleOutline />}
          size="small"
          onClick={addRow}
          disabled={disabled}
        >
          Ajouter un tamis
        </Button>
      </Box>

      <Stack direction="row" spacing={4} sx={{ mt: 2 }}>
        <Kpi label="Masse totale" value={`${computed.total_mass.toFixed(2)} g`} />
        <Kpi
          label="D80"
          value={computed.d80 !== null ? `${computed.d80} μm` : '—'}
          highlight
        />
        <Kpi
          label="D50"
          value={computed.d50 !== null ? `${computed.d50} μm` : '—'}
          highlight
        />
      </Stack>

      {computed.total_mass === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Saisissez les masses pour calculer la granulométrie.
        </Alert>
      )}

      <TextField
        fullWidth
        multiline
        minRows={2}
        sx={{ mt: 2 }}
        label="Notes tamisage"
        value={notes}
        disabled={disabled}
        onChange={(e) => setNotes(e.target.value)}
      />
    </Paper>
  );
};

const Kpi: React.FC<{ label: string; value: string; highlight?: boolean }> = ({
  label,
  value,
  highlight,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="h6"
      color={highlight ? 'primary.main' : undefined}
      sx={{ fontWeight: highlight ? 700 : 500 }}
    >
      {value}
    </Typography>
  </Box>
);

export default TamisageSubForm;
